import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ConfirmModal from '../components/ConfirmModal';
import VerifiedFlame from '../components/VerifiedFlame';
import { AthleteProfileShell } from '../components/ProfileShell';
import Button from '../components/Button';
import SchoolLevelSection from '../components/SchoolLevelSection';
import { saveErrorText } from '../constants/addressRegions';

export default function AcademicsPage() {
  const { user, API_URL } = useAuth();
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [profileUrl, setProfileUrl] = useState('');
  const [previewModal, setPreviewModal] = useState(null);

  const [profile, setProfile] = useState({
    high_school: '',
    high_school_city: '',
    high_school_state: '',
    high_school_zip: '',
    graduation_year: '',
    honors: false,
    ncaa_eligibility_id: '',
    gpa_weighted: '',
    gpa_unweighted: '',
    act_score: '',
    sat_score: '',
    school_logo: null,
    school_level: 'high_school',
  });

  const [verified, setVerified] = useState({
    gpa_weighted: false,
    gpa_unweighted: false,
    honors: false,
    act_score: false,
    sat_score: false
  });

  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [confirmAction, setConfirmAction] = useState(null);

  // High school autocomplete state
  const [schoolsData, setSchoolsData] = useState([]);
  const [draftCollege, setDraftCollege] = useState(false);

  const getAxiosConfig = () => ({
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  });

  const fetchDocuments = async () => {
    try {
      const res = await axios.get(`${API_URL}/athletes/documents`, getAxiosConfig());
      setDocuments(res.data || []);
    } catch (err) {
      console.error('Error fetching documents:', err);
    }
  };

  const [uploadCategory, setUploadCategory] = useState('transcript');
  const [showUploadPicker, setShowUploadPicker] = useState(false);
  const transcriptInputRef = useRef(null);
  const testScoreInputRef = useRef(null);
  const uploadPickerRef = useRef(null);

  const handleDocUpload = async (e, category = 'transcript') => {
    const file = e.target.files[0];
    if (!file) return;

    const usedSlots = documents.map(d => d.slot_number);
    let nextSlot = 1;
    while (usedSlots.includes(nextSlot) && nextSlot <= 8) nextSlot++;
    if (nextSlot > 8) {
      setMessage('Maximum 8 documents reached');
      setMessageType('error');
      setTimeout(() => setMessage(''), 4000);
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('slot_number', nextSlot);
    formData.append('label', file.name);
    formData.append('category', category);

    try {
      await axios.post(`${API_URL}/athletes/documents/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setMessage('Document uploaded');
      setMessageType('');
      setTimeout(() => setMessage(''), 3000);
      fetchDocuments();
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Upload failed');
      setMessageType('error');
      setTimeout(() => setMessage(''), 4000);
    } finally {
      setUploading(false);
      if (transcriptInputRef.current) transcriptInputRef.current.value = '';
      if (testScoreInputRef.current) testScoreInputRef.current.value = '';
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Close upload picker when clicking outside
  useEffect(() => {
    if (!showUploadPicker) return;
    const handleClick = (e) => {
      if (uploadPickerRef.current && !uploadPickerRef.current.contains(e.target)) {
        setShowUploadPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showUploadPicker]);

  const handleDocDelete = async (docId) => {
    try {
      await axios.delete(`${API_URL}/athletes/documents/${docId}`, getAxiosConfig());
      setMessage('Document deleted');
      setMessageType('');
      setTimeout(() => setMessage(''), 3000);
      fetchDocuments();
    } catch (err) {
      console.error('Error deleting document:', err);
    }
  };

  const handleDocDownload = (doc) => {
    fetch(`${API_URL}/athletes/documents/${doc.id}/download`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    })
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.filename;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(err => console.error('Download error:', err));
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSchoolLogoUpload = async () => {
    if (!profile.high_school || !profile.high_school_state) {
      setMessage('Please set your school and state before uploading a logo');
      setMessageType('error');
      setTimeout(() => setMessage(''), 4000);
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('photo_type', 'school_logo');

      try {
        await axios.post(`${API_URL}/athletes/upload-photo`, formData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        setMessage('Logo uploaded');
        setMessageType('');
        setTimeout(() => setMessage(''), 3000);
        fetchProfile();
      } catch (error) {
        setMessage(error.response?.data?.detail || 'Error uploading logo');
        setMessageType('error');
        setTimeout(() => setMessage(''), 4000);
      }
    };
    input.click();
  };

  useEffect(() => {
    fetchProfile();
    fetchDocuments();
    fetch('/data/high-schools.json')
      .then(r => r.json())
      .then(data => setSchoolsData(data))
      .catch(err => console.error('Error loading schools data:', err));
  }, []);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') setPreviewModal(null); };
    if (previewModal) document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [previewModal]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/athletes/profile`, getAxiosConfig());
      if (response.data) {
        setProfile(prev => ({ ...prev, ...response.data }));
        if (response.data.profile_url) setProfileUrl(response.data.profile_url);
        setVerified({
          gpa_weighted: response.data.gpa_weighted_verified || false,
          gpa_unweighted: response.data.gpa_unweighted_verified || false,
          honors: response.data.honors_verified || false,
          act_score: response.data.act_score_verified || false,
          sat_score: response.data.sat_score_verified || false
        });
      }
    } catch (error) {
      console.log('No existing profile or error:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const saveField = async (name, value) => {
    try {
      await axios.post(
        `${API_URL}/athletes/profile`,
        { [name]: value },
        getAxiosConfig()
      );
      setMessage('Saved');
      setMessageType('');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Auto-save error:', error);
      setMessage('Error saving');
      setMessageType('error');
      setTimeout(() => setMessage(''), 4000);
    }
  };

  const handleBlur = async (e) => {
    const { name } = e.target;
    const currentValue = profile[name];

    if ((name === 'gpa_weighted' || name === 'gpa_unweighted' || name === 'act_score' || name === 'sat_score') && verified[name]) {
      setConfirmAction({
        title: 'Unverify Stat',
        message: 'This stat is currently verified. Changing it will mark it as unverified and you will not be able to return to the verified stat unless verified by ProZpects staff or Partner.',
        confirmLabel: 'Continue',
        danger: true,
        onConfirm: async () => {
          setConfirmAction(null);
          setVerified(prev => ({ ...prev, [name]: false }));
          await saveField(name, currentValue);
        },
        onCancel: () => {
          setConfirmAction(null);
          fetchProfile();
        }
      });
      return;
    }

    if (name === 'honors' && verified.honors) {
      setConfirmAction({
        title: 'Unverify Stat',
        message: 'This stat is currently verified. Changing it will mark it as unverified and you will not be able to return to the verified stat unless verified by ProZpects staff or Partner.',
        confirmLabel: 'Continue',
        danger: true,
        onConfirm: async () => {
          setConfirmAction(null);
          setVerified(prev => ({ ...prev, honors: false }));
          await saveField(name, currentValue);
        },
        onCancel: () => {
          setConfirmAction(null);
          fetchProfile();
        }
      });
      return;
    }

    await saveField(name, currentValue);
  };

  // School block: SchoolLevelSection decides WHAT to save; this page persists it
  // and shows the server's readable 400 detail (e.g. the college eligibility
  // rule). Resolves true on success, false on failure.
  const handleSchoolSave = async (fields) => {
    try {
      await axios.post(`${API_URL}/athletes/profile`, fields, getAxiosConfig());
      setProfile(prev => {
        const next = { ...prev, ...fields };
        if ('high_school' in fields) {
          // The picker's list drops the logo, so look it up from the loaded file.
          const pick = 'high_school_city' in fields;
          const match = schoolsData.find(s => s.n === fields.high_school && (!pick
            || ((s.c || '').toLowerCase() === (fields.high_school_city || '').toLowerCase()
              && s.s === fields.high_school_state)));
          next.school_logo = match?.l || null;
        }
        return next;
      });
      // A level switch changes fields the server sets (logo, cleared school).
      if ('school_level' in fields) fetchProfile();
      setMessage('Saved'); setMessageType(''); setTimeout(() => setMessage(''), 3000);
      return true;
    } catch (error) {
      console.error('Auto-save error:', error);
      setMessage(saveErrorText(error)); setMessageType('error'); setTimeout(() => setMessage(''), 4000);
      return false;
    }
  };

  // Saving {school_level:'high_school'} wipes college, unitid and eligibility on
  // the server with no undo, so ask first. Resolves true on Continue.
  const confirmLeaveCollege = () => new Promise((resolve) => {
    setConfirmAction({
      title: 'Leave college?',
      message: 'This removes your college and eligibility. Continue?',
      confirmLabel: 'Continue',
      danger: true,
      onConfirm: () => { setConfirmAction(null); resolve(true); },
      onCancel: () => { setConfirmAction(null); resolve(false); },
    });
  });

  return (
    <AthleteProfileShell activeTab="Academics">
      {message && (
        <div className={`settings-toast ${messageType === 'error' ? 'settings-toast--error' : ''}`} role="alert">{message}</div>
      )}

      {/* School Information */}
      <div className="settings-section">
        <h2 className="settings-section__title">School Information</h2>
        <p className="settings-section__desc">Your school details for recruiting profiles.</p>
        <SchoolLevelSection
          profile={profile}
          onSave={handleSchoolSave}
          onModeChange={setDraftCollege}
          confirmLeaveCollege={confirmLeaveCollege}
        />
        <div className="form-row form-row--grad-honors">
          <div className="form-field">
            <label className="form-field__label" htmlFor="graduation_year">Grad Year</label>
            <select
              id="graduation_year"
              name="graduation_year"
              value={profile.graduation_year}
              onChange={handleChange}
              onBlur={handleBlur}
              className="form-field__select"
            >
              <option value="">Select...</option>
              {Array.from({ length: 11 }, (_, i) => new Date().getFullYear() + i).map(yr => (
                <option key={yr} value={yr}>{yr}</option>
              ))}
            </select>
          </div>
          <div className="form-field" style={{ display: 'flex', alignItems: 'center', paddingTop: '24px', flexShrink: 0 }}>
            <label className="settings-field__checkbox-label" style={{ marginBottom: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input
                type="checkbox"
                name="honors"
                checked={profile.honors}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <span style={{ lineHeight: 1.1, textAlign: 'center' }}>Honors<br />Student</span>
              {verified.honors && (
                <VerifiedFlame />
              )}
            </label>
          </div>
        </div>
        {profile.school_level !== 'college' && !draftCollege && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '16px', padding: '12px 16px', background: 'var(--ed-surface-soft)', borderRadius: '8px' }}>
          {profile.school_logo ? (
            <img
              src={profile.school_logo}
              alt="School Logo"
              style={{ width: '56px', height: '56px', objectFit: 'contain', borderRadius: '8px', border: '1px solid var(--ed-rule)', cursor: 'pointer' }}
              onClick={handleSchoolLogoUpload}
              title="Click to change logo"
            />
          ) : (
            <div
              style={{
                width: '56px', height: '56px', borderRadius: '8px',
                border: '2px dashed var(--ed-rule)', display: 'flex', alignItems: 'center',
                justifyContent: 'center', color: 'var(--ed-on-light-subtle)', fontSize: '11px', textAlign: 'center',
                cursor: 'pointer', flexShrink: 0
              }}
              onClick={handleSchoolLogoUpload}
              title="Upload school logo"
            >
              Logo
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--ed-on-light)' }}>School Logo</div>
            <div style={{ fontSize: '11px', color: 'var(--ed-on-light-subtle)', marginTop: '2px' }}>Shared for all athletes at this school</div>
          </div>
          <Button variant="secondary" size="sm" style={{ flexShrink: 0 }} onClick={handleSchoolLogoUpload}>
            {profile.school_logo ? 'Change' : 'Upload'}
          </Button>
        </div>
        )}
      </div>

      {/* Academic */}
      <div className="settings-section">
        <h2 className="settings-section__title">Academic</h2>
        <p className="settings-section__desc">GPA and test scores for academic eligibility.</p>
        <div className="form-row">
          <div className="form-field">
            <label className="form-field__label" htmlFor="gpa_weighted">
              Weighted GPA
              {verified.gpa_weighted && (
                <VerifiedFlame />
              )}
            </label>
            <input
              type="number"
              step="0.01"
              id="gpa_weighted"
              name="gpa_weighted"
              value={profile.gpa_weighted}
              onChange={handleChange}
              onBlur={handleBlur}
              className="form-field__input"
              placeholder="4.0"
            />
          </div>
          <div className="form-field">
            <label className="form-field__label" htmlFor="gpa_unweighted">
              Unweighted GPA
              {verified.gpa_unweighted && (
                <VerifiedFlame />
              )}
            </label>
            <input
              type="number"
              step="0.01"
              id="gpa_unweighted"
              name="gpa_unweighted"
              value={profile.gpa_unweighted}
              onChange={handleChange}
              onBlur={handleBlur}
              className="form-field__input"
              placeholder="3.8"
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label className="form-field__label" htmlFor="act_score">
              ACT Score
              {verified.act_score && (
                <VerifiedFlame />
              )}
            </label>
            <input
              type="number"
              step="1"
              id="act_score"
              name="act_score"
              value={profile.act_score}
              onChange={handleChange}
              onBlur={handleBlur}
              className="form-field__input"
              placeholder="25"
            />
          </div>
          <div className="form-field">
            <label className="form-field__label" htmlFor="sat_score">
              SAT Score
              {verified.sat_score && (
                <VerifiedFlame />
              )}
            </label>
            <input
              type="number"
              step="10"
              id="sat_score"
              name="sat_score"
              value={profile.sat_score}
              onChange={handleChange}
              onBlur={handleBlur}
              className="form-field__input"
              placeholder="1200"
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label className="form-field__label" htmlFor="ncaa_eligibility_id">NCAA Eligibility ID</label>
            <input
              type="text"
              inputMode="numeric"
              id="ncaa_eligibility_id"
              name="ncaa_eligibility_id"
              value={profile.ncaa_eligibility_id || ''}
              onChange={(e) => setProfile(prev => ({ ...prev, ncaa_eligibility_id: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
              onBlur={handleBlur}
              className="form-field__input"
              placeholder="10-digit NCAA ID"
            />
          </div>
          <div className="form-field" />
        </div>

        {/* Transcripts / Test Scores */}
        <div style={{ marginTop: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '12px', marginBottom: '8px' }}>
            <span className="form-field__label" style={{ marginBottom: 0 }}>Transcripts / Test Scores</span>
            {documents.length < 8 && (
              <div style={{ position: 'relative' }} ref={uploadPickerRef}>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowUploadPicker(!showUploadPicker)}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
                {showUploadPicker && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, marginTop: '4px', zIndex: 10,
                    background: 'var(--ed-surface)', border: '1px solid var(--ed-rule)', borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)', overflow: 'hidden', minWidth: '160px',
                  }}>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => { setShowUploadPicker(false); transcriptInputRef.current?.click(); }}
                      onMouseEnter={(e) => e.target.style.background = 'var(--ed-surface-soft)'}
                      onMouseLeave={(e) => e.target.style.background = 'none'}
                    >
                      Transcript
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => { setShowUploadPicker(false); testScoreInputRef.current?.click(); }}
                      onMouseEnter={(e) => e.target.style.background = 'var(--ed-surface-soft)'}
                      onMouseLeave={(e) => e.target.style.background = 'none'}
                    >
                      Test Score
                    </Button>
                  </div>
                )}
              </div>
            )}
            <input
              ref={transcriptInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={(e) => handleDocUpload(e, 'transcript')}
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.txt,.xls,.xlsx"
            />
            <input
              ref={testScoreInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={(e) => handleDocUpload(e, 'test_score')}
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.txt,.xls,.xlsx"
            />
          </div>
          {documents.length === 0 && (
            <p style={{ color: 'var(--ed-on-light-subtle)', fontSize: '13px' }}>No documents uploaded yet.</p>
          )}
          {documents.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {documents.map(doc => (
                <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: 'var(--ed-surface-soft)', borderRadius: '6px', fontSize: '13px' }}>
                  <span style={{
                    fontSize: '11px', fontWeight: 600, padding: '2px 6px', borderRadius: '4px', flexShrink: 0,
                    background: doc.category === 'test_score' ? '#fef3c7' : '#dbeafe',
                    color: doc.category === 'test_score' ? '#92400e' : '#1e40af',
                  }}>
                    {doc.category === 'test_score' ? 'Test Score' : 'Transcript'}
                  </span>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.label || doc.filename}</span>
                  <span style={{ color: 'var(--ed-on-light-subtle)', flexShrink: 0 }}>{formatFileSize(doc.file_size)}</span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => handleDocDownload(doc)}>Download</Button>
                  <Button type="button" variant="danger" size="sm" onClick={() => handleDocDelete(doc.id)}>Delete</Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {confirmAction && (
        <ConfirmModal
          title={confirmAction.title}
          message={confirmAction.message}
          confirmLabel={confirmAction.confirmLabel}
          danger={confirmAction.danger}
          onConfirm={confirmAction.onConfirm}
          onCancel={confirmAction.onCancel || (() => setConfirmAction(null))}
        />
      )}
      {previewModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setPreviewModal(null)}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'relative', width: '90vw', height: '90vh', backgroundColor: '#fff', borderRadius: '12px', overflow: 'hidden' }}
            onClick={(e) => e.stopPropagation()}>
            <Button onClick={() => setPreviewModal(null)} aria-label="Close preview" variant="secondary" iconOnly
              style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 10 }}>
              &times;
            </Button>
            <iframe
              src={previewModal === 'card' ? `/athlete/${user?.uid || '0'}/${profileUrl}/card` : `/athlete/${user?.uid || '0'}/${profileUrl}`}
              title={previewModal === 'card' ? 'Player Card Preview' : 'Public Profile Preview'}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          </div>
        </div>
      )}
    </AthleteProfileShell>
  );
}
